import { ChatWithUsIcon, FAQIcon, MailIcon, NavCloseIcon } from 'assets/icons';
import { CustomModal, Loader } from 'components';
import { useGetTeamHelpQuery } from 'containers/app/api';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { FC, useCallback, useEffect } from 'react';
import { VoidFn } from 'types/baseTypes';
import { SUPPORT_MAIL } from 'config';
import { openScanpayFAQInNewTab } from 'utils/urlUtils';
import HelpSupportRow from './HelpSupportRow';

const CSM_AVAILABILITY_TEXT = 'Mon-Fri: 9AM - 6PM (EST)';
const EMAIL_COPIED_MESSAGE = 'Email copied to clipboard.';

interface HelpSupportPopupProps {
  open: boolean;
  onClose: VoidFn;
}

const HelpSupportPopup: FC<HelpSupportPopupProps> = ({ open, onClose }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const teamId = currentTeam?.id ?? null;
  const { data: helpData, isLoading } = useGetTeamHelpQuery(
    { teamId: teamId ?? '' },
    { skip: !open || teamId === null }
  );

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, handleEscape]);

  const handleFaqClick = () => {
    openScanpayFAQInNewTab();
  };

  const handleChatClick = () => {
    if (typeof window !== 'undefined' && window.fcWidget) window.fcWidget.open();
    onClose();
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${helpData?.customerCareEmail ?? SUPPORT_MAIL}`;
    onClose();
  };

  if (!open) return null;

  return (
    <CustomModal open={open} width="md:max-w-[480px] md:w-[480px]" className="max-h-[90vh] overflow-y-auto">
      <div className="relative p-6" role="dialog" aria-label="Help and Support">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2.5 top-2.5 cursor-pointer rounded p-1 hover:bg-borderGray"
          aria-label="Close Help and Support">
          <NavCloseIcon />
        </button>

        <h2 className="mb-6 pr-8 text-17px font-semibold text-primaryText">Help & Support</h2>

        {isLoading && (
          <div className="flex min-h-[200px] items-center justify-center py-8">
            <Loader />
          </div>
        )}

        {!isLoading && (
          <div className="flex flex-col gap-6">
            <section aria-label="How can we help you">
              <h3 className="mb-3 text-sbase font-semibold text-primaryText">How can we help you?</h3>
              <div className="flex flex-col gap-2">
                <HelpSupportRow
                  icon={<FAQIcon className="h-5 w-5" />}
                  title="Frequently Asked Questions"
                  subtitle="Find answers to common questions"
                  onClick={handleFaqClick}
                  ariaLabel="Open Frequently Asked Questions"
                />
              </div>
            </section>

            <section aria-label="Need more help">
              <h3 className="mb-3 text-sbase font-semibold text-primaryText">Need more help?</h3>
              <div className="flex flex-col gap-2">
                {helpData?.customerCareEmail && (
                  <HelpSupportRow
                    icon={<MailIcon className="path-fill-current h-5 w-5 text-accentText" />}
                    title="Email us"
                    subtitle={helpData?.customerCareEmail ?? SUPPORT_MAIL}
                    onClick={handleEmailClick}
                    ariaLabel="Email us"
                    showCopyIcon={true}
                    copyMessage={EMAIL_COPIED_MESSAGE}
                  />
                )}
                <HelpSupportRow
                  icon={<ChatWithUsIcon className="h-5 w-5" />}
                  title="Chat with us"
                  subtitle={CSM_AVAILABILITY_TEXT}
                  onClick={handleChatClick}
                  ariaLabel="Chat with us"
                />
              </div>
            </section>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default HelpSupportPopup;
